import * as XLSX from 'xlsx';
import type { PaginatedResponse } from './api';

const EXPORT_PAGE_SIZE = 200;
const EXPORT_CONCURRENCY = 3;

/** Build the worksheet incrementally; never return a partial export. */
export async function buildTransactionExportSheet<T>(
    fetchPage: (page: number, size: number) => Promise<Pick<PaginatedResponse<T>, 'items' | 'total'>>,
    toRow: (transaction: T) => Record<string, unknown>,
    onProgress: (loaded: number, total: number) => void,
    options: { pageSize?: number; getId?: (transaction: T) => string } = {},
): Promise<XLSX.WorkSheet> {
    const pageSize = options.pageSize ?? EXPORT_PAGE_SIZE;
    const getId = options.getId ?? ((transaction: T) => (transaction as { transaction_id: string }).transaction_id);
    if (!Number.isInteger(pageSize) || pageSize < 1) throw new Error('Некорректный размер порции экспорта.');
    const sheet = XLSX.utils.json_to_sheet([]);
    const ids = new Set<string>();
    let total: number | undefined;
    const appendPage = (response: Pick<PaginatedResponse<T>, 'items' | 'total'>, page: number) => {
        if (!Array.isArray(response.items) || !Number.isInteger(response.total) || response.total < 0) {
            throw new Error('Сервер вернул некорректные данные для экспорта.');
        }
        total = total ?? response.total;
        const expected = Math.min(pageSize, total - ids.size);
        if (response.total !== total || response.items.length !== expected) {
            throw new Error('Список транзакций изменился или получен не полностью. Выберите завершённый период и повторите экспорт.');
        }
        for (const transaction of response.items) {
            const id = getId(transaction);
            if (typeof id !== 'string' || !id || ids.has(id)) {
                throw new Error('Список транзакций изменился во время экспорта. Повторите экспорт за завершённый период.');
            }
            ids.add(id);
        }
        XLSX.utils.sheet_add_json(sheet, response.items.map(toRow), {
            origin: page === 1 ? 0 : -1,
            skipHeader: page !== 1,
        });
        onProgress(ids.size, total);
    };
    // The first page establishes the count. Keep bounded batches in page order,
    // even if their requests finish out of order; drain errors before returning.
    appendPage(await fetchPage(1, pageSize), 1);
    const pages = Math.ceil(total! / pageSize);
    for (let start = 2; start <= pages; start += EXPORT_CONCURRENCY) {
        const pageNumbers = Array.from(
            { length: Math.min(EXPORT_CONCURRENCY, pages - start + 1) },
            (_, index) => start + index,
        );
        const results = await Promise.allSettled(pageNumbers.map(page => fetchPage(page, pageSize)));
        for (const result of results) {
            if (result.status === 'rejected') throw result.reason;
        }
        results.forEach((result, index) => {
            if (result.status === 'fulfilled') appendPage(result.value, pageNumbers[index]);
        });
    }
    return sheet;
}
